import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import api, { errMsg } from "../api";
import { Megaphone, AlertTriangle, Loader2, Trash2, CheckCircle2, Paperclip, History } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function AnnouncementsPage() {
    const { lang, user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showHistory, setShowHistory] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const url = showHistory ? "/announcements?include_expired=true" : "/announcements";
            const { data } = await api.get(url);
            setItems(data);
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setLoading(false);
        }
    }, [showHistory]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const h = () => load();
        window.addEventListener("mytacs:tick", h);
        return () => window.removeEventListener("mytacs:tick", h);
    }, [load]);

    const isPriv = user?.role === "admin" || user?.role === "master";

    const remove = async (id) => {
        if (!window.confirm(t(lang, "confirmDelete") || "Sure?")) return;
        try {
            await api.delete(`/announcements/${id}`);
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    return (
        <div className="px-4 sm:px-8 py-8 max-w-4xl" data-testid="announcements-page">
            <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1.5">
                        <Megaphone className="w-3 h-3" /> {t(lang, "announcements")}
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold mt-1">{t(lang, "announcements")}</h1>
                    <p className="text-sm text-neutral-500 mt-1">{t(lang, "announcementDescr")}</p>
                </div>
                {isPriv && (
                    <Button
                        variant={showHistory ? "default" : "outline"}
                        size="sm"
                        onClick={() => setShowHistory((v) => !v)}
                        className="gap-1.5"
                        data-testid="ann-history-toggle"
                    >
                        <History className="w-3.5 h-3.5" />
                        {lang === "de" ? (showHistory ? "Verlauf an" : "Verlauf anzeigen") : (showHistory ? "History on" : "Show history")}
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                </div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-neutral-300 rounded-lg py-16 text-center">
                    <Megaphone className="w-12 h-12 mx-auto text-neutral-300 mb-2" />
                    <p className="text-neutral-500">{t(lang, "noNotifications")}</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((a) => (
                        <article
                            key={a.id}
                            data-testid={`announcement-${a.id}`}
                            className={`p-5 rounded-lg border-2 bg-white hover:shadow-sm transition ${a.urgent ? "border-red-300" : "border-neutral-200"}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {a.urgent ? (
                                            <AlertTriangle className="w-4 h-4 text-red-600" />
                                        ) : (
                                            <Megaphone className="w-4 h-4 text-neutral-700" />
                                        )}
                                        <h2 className={`font-display text-lg font-bold ${a.urgent ? "text-red-700" : ""}`}>{a.title}</h2>
                                        {a.urgent && (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-1.5 py-0.5 rounded">{t(lang, "urgent")}</span>
                                        )}
                                        {a.is_expired && (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded">{lang === "de" ? "Abgelaufen" : "Expired"}</span>
                                        )}
                                        {a.expires_at && !a.is_expired && (
                                            <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded" title={new Date(a.expires_at).toLocaleString(LOCALES[lang])}>
                                                ⏳ {lang === "de" ? "läuft" : "expires"} {new Date(a.expires_at).toLocaleDateString(LOCALES[lang])}
                                            </span>
                                        )}
                                        {a.acknowledged && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                                <CheckCircle2 className="w-2.5 h-2.5" /> {lang === "de" ? "Gelesen" : lang === "tr" ? "Okundu" : "Read"}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-neutral-500 mt-1">
                                        {t(lang, "from")} <span className="font-semibold text-neutral-700">{a.created_by_name}</span>
                                        {" · "}
                                        {new Date(a.created_at).toLocaleString(LOCALES[lang])}
                                        {" · "}
                                        {(a.acknowledged_by || []).length} {lang === "de" ? "haben gelesen" : lang === "tr" ? "okudu" : "read it"}
                                    </p>
                                </div>
                                {isPriv && (
                                    <button
                                        onClick={() => remove(a.id)}
                                        data-testid={`delete-ann-${a.id}`}
                                        className="text-neutral-400 hover:text-red-600 p-1 shrink-0"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            <p className="text-sm text-neutral-800 mt-3 whitespace-pre-wrap leading-relaxed">{a.content}</p>
                            {(a.attachments || []).length > 0 && (
                                <div className="mt-3 space-y-1" data-testid={`ann-attachments-${a.id}`}>
                                    {a.attachments.map((att) => (
                                        <a
                                            key={att.id}
                                            href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${att.id}/download?auth=${encodeURIComponent(localStorage.getItem("taskflow_token") || "")}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`ann-att-${att.id}`}
                                            className="flex items-center gap-2 text-xs bg-neutral-50 border border-neutral-200 rounded px-2 py-1.5 hover:bg-neutral-100 max-w-md"
                                        >
                                            <Paperclip className="w-3 h-3 text-neutral-500" />
                                            <span className="flex-1 truncate text-blue-700 underline">{att.name}</span>
                                            <span className="text-neutral-400 text-[10px]">{Math.round((att.size || 0) / 1024)} KB</span>
                                        </a>
                                    ))}
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
